/** Draft persistence for the public listing wizard (/listing) and /add-property. */

/**
 * The draft used to live in sessionStorage, which is per-tab and is wiped by
 * sessionStorage.clear() on logout / session expiry. A new tab, a reopened tab
 * or a cleared session restarted the wizard at step 1 and re-sent signup.
 * Keys now live in localStorage under a prefix and expire after DRAFT_TTL_MS.
 */
const KEY_PREFIX = 'listingDraft:';
const DRAFT_TS_KEY = 'updatedAt';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function safeLocalStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function safeSessionStorage() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export const draftStore = {
  getItem(key) {
    const ls = safeLocalStorage();
    return ls ? ls.getItem(KEY_PREFIX + key) : null;
  },
  setItem(key, value) {
    const ls = safeLocalStorage();
    if (!ls) return;
    try {
      ls.setItem(KEY_PREFIX + key, String(value));
      if (key !== DRAFT_TS_KEY) ls.setItem(KEY_PREFIX + DRAFT_TS_KEY, String(Date.now()));
    } catch {
      /* storage full or disabled */
    }
  },
  removeItem(key) {
    const ls = safeLocalStorage();
    if (ls) ls.removeItem(KEY_PREFIX + key);
  },
};

export const STEPS = {
  ACCOUNT: 1,
  TYPE: 2,
  LOCATION: 3,
  DETAILS: 4,
  AMENITIES: 5,
  IMAGES: 6,
  ROOMS: 7,
  KYC: 8,
  REVIEW: 9,
};
export const LAST_STEP = STEPS.REVIEW;

/** Short labels for the listing sidebar stepper, in wizard order. */
export const WIZARD_STEPS = [
  { id: STEPS.ACCOUNT, label: 'Account' },
  { id: STEPS.TYPE, label: 'Type' },
  { id: STEPS.LOCATION, label: 'Location' },
  { id: STEPS.DETAILS, label: 'Details' },
  { id: STEPS.AMENITIES, label: 'Amenities' },
  { id: STEPS.IMAGES, label: 'Photos' },
  { id: STEPS.ROOMS, label: 'Rooms' },
  { id: STEPS.KYC, label: 'Profile' },
  { id: STEPS.REVIEW, label: 'Publish' },
];

export function getWizardSteps(mode) {
  return WIZARD_STEPS.filter((step) => mode !== 'add-property' || step.id !== STEPS.ACCOUNT);
}

export function getStepOrdinal(mode, stepId) {
  const steps = getWizardSteps(mode);
  const idx = steps.findIndex((step) => step.id === stepId);
  return { current: idx >= 0 ? idx + 1 : 1, total: steps.length };
}

export const LISTING_FIELD_KEYS = [
  'fullName', 'accountEmail', 'accountPhone', 'password', 'confirmPassword',
  'propertyName', 'selectedPropertyType', 'address', 'city', 'state', 'country', 'pincode',
  'latitude', 'longitude', 'placeSearchInput',
  'propertyDescription', 'phoneNumber', 'email', 'checkinTime', 'checkoutTime',
  'selectedAmenities', 'selectedHotelPolicies', 'selectedCancellationPolicies',
  'isOpen24x7', 'isCoupleFriendly',
  'panNumber', 'gstNumber', 'gstActive', 'agreedToPolicy',
  'roomTypes',
  // Legacy single-room keys (pre multi-room). Read once for migration, then removed.
  'roomTypeName', 'roomTypeCategoryId', 'roomCount', 'maxOccupancy', 'occupancyRates',
  'selectedRoomAmenities', 'rateStartDate', 'rateEndDate',
  'verifiedEmail', 'emailVerifiedSaved',
  'accountCreatedEmail', 'accountHostId',
];

export const STEP_KEY_SIGNUP = 'onboardingCurrentStep';
export const STEP_KEY_ADD_PROPERTY = 'addPropertyCurrentStep';
export const ACCOUNT_COMPLETE_KEY = 'onboardingAccountComplete';
const ACCOUNT_EMAIL_KEY = 'accountCreatedEmail';
const ACCOUNT_HOST_ID_KEY = 'accountHostId';

const LEGACY_ROOM_KEYS = [
  'roomTypeName', 'roomTypeCategoryId', 'roomCount', 'maxOccupancy',
  'occupancyRates', 'selectedRoomAmenities', 'rateStartDate', 'rateEndDate',
];

const ALL_DRAFT_KEYS = [...LISTING_FIELD_KEYS, STEP_KEY_SIGNUP, STEP_KEY_ADD_PROPERTY, ACCOUNT_COMPLETE_KEY];

const JSON_FIELD_KEYS = new Set([
  'selectedAmenities',
  'selectedHotelPolicies',
  'selectedCancellationPolicies',
  'selectedRoomAmenities',
  'occupancyRates',
  'roomTypes',
]);

function str(v) {
  return v === undefined || v === null ? '' : String(v);
}

function hasText(v) {
  return v != null && String(v).trim() !== '';
}

function normalizeEmail(v) {
  return str(v).trim().toLowerCase();
}

function parseJsonArray(key) {
  try {
    const v = JSON.parse(draftStore.getItem(key) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function parseJsonObject(key) {
  try {
    const v = JSON.parse(draftStore.getItem(key) || '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch {
    return {};
  }
}

export function ymdToday() {
  return new Date().toISOString().slice(0, 10);
}

export function ymdPlusYears(years) {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

/* ---------------------------------------------------------------- rooms */

let roomSeq = 0;

export function createEmptyRoom() {
  roomSeq += 1;
  return {
    id: `room_${Date.now().toString(36)}_${roomSeq}`,
    roomTypeName: '',
    roomTypeCategoryId: '',
    roomTypeSafeName: '',
    roomCount: '1',
    maxOccupancy: '2',
    occupancyRates: { '1guest': '', '2guest': '' },
    selectedRoomAmenities: [],
    rateStartDate: ymdToday(),
    rateEndDate: ymdPlusYears(1),
  };
}

export function normalizeRoom(raw) {
  const base = createEmptyRoom();
  if (!raw || typeof raw !== 'object') return base;
  const rates = raw.occupancyRates && typeof raw.occupancyRates === 'object' && !Array.isArray(raw.occupancyRates)
    ? raw.occupancyRates
    : base.occupancyRates;
  return {
    ...base,
    id: str(raw.id) || base.id,
    roomTypeName: str(raw.roomTypeName),
    roomTypeCategoryId: str(raw.roomTypeCategoryId),
    roomTypeSafeName: str(raw.roomTypeSafeName),
    roomCount: str(raw.roomCount) || base.roomCount,
    maxOccupancy: str(raw.maxOccupancy) || base.maxOccupancy,
    occupancyRates: rates,
    selectedRoomAmenities: Array.isArray(raw.selectedRoomAmenities) ? raw.selectedRoomAmenities : [],
    rateStartDate: str(raw.rateStartDate) || base.rateStartDate,
    rateEndDate: str(raw.rateEndDate) || base.rateEndDate,
  };
}

export function loadStoredRooms() {
  return parseJsonArray('roomTypes').map(normalizeRoom);
}

function roomHasAnyInput(room) {
  return hasText(room?.roomTypeName) || hasText(room?.roomTypeCategoryId);
}

/* ------------------------------------------------------------ migration */

/** One-time copy of a pre-localStorage draft left in sessionStorage. */
function migrateLegacySessionDraft() {
  const ss = safeSessionStorage();
  if (!ss) return;
  ALL_DRAFT_KEYS.forEach((key) => {
    const v = ss.getItem(key);
    if (v == null) return;
    if (draftStore.getItem(key) == null) draftStore.setItem(key, v);
    ss.removeItem(key);
  });
}

/** Pre multi-room drafts stored a single room in flat keys; fold it into roomTypes. */
function migrateLegacySingleRoom() {
  if (draftStore.getItem('roomTypes') == null) {
    const name = draftStore.getItem('roomTypeName');
    const catId = draftStore.getItem('roomTypeCategoryId');
    if (hasText(name) || hasText(catId)) {
      const room = normalizeRoom({
        roomTypeName: name,
        roomTypeCategoryId: catId,
        roomCount: draftStore.getItem('roomCount'),
        maxOccupancy: draftStore.getItem('maxOccupancy'),
        occupancyRates: parseJsonObject('occupancyRates'),
        selectedRoomAmenities: parseJsonArray('selectedRoomAmenities'),
        rateStartDate: draftStore.getItem('rateStartDate'),
        rateEndDate: draftStore.getItem('rateEndDate'),
      });
      draftStore.setItem('roomTypes', JSON.stringify([room]));
    }
  }
  LEGACY_ROOM_KEYS.forEach((key) => draftStore.removeItem(key));
}

function purgeExpiredDraft() {
  const raw = draftStore.getItem(DRAFT_TS_KEY);
  if (raw == null) return;
  const ts = Number(raw);
  if (Number.isFinite(ts) && Date.now() - ts > DRAFT_TTL_MS) clearListingDraft();
}

/** Run before reading the draft: migrates legacy storage and drops expired drafts. */
export function prepareDraftStore() {
  migrateLegacySessionDraft();
  purgeExpiredDraft();
  migrateLegacySingleRoom();
}

/* ---------------------------------------------------------------- steps */

export function getStepKey(mode) {
  return mode === 'add-property' ? STEP_KEY_ADD_PROPERTY : STEP_KEY_SIGNUP;
}

export function getFirstStep(mode) {
  return mode === 'add-property' ? STEPS.TYPE : STEPS.ACCOUNT;
}

export function getTotalSteps(mode) {
  return LAST_STEP - getFirstStep(mode) + 1;
}

export function progressPercent(mode, currentStep) {
  const first = getFirstStep(mode);
  const total = getTotalSteps(mode);
  const idx = Math.max(0, Math.min(total, currentStep - first + 1));
  return (idx / total) * 100;
}

export function saveCurrentStep(mode, step) {
  draftStore.setItem(getStepKey(mode), String(step));
}

export function loadSavedStep(mode) {
  const raw = draftStore.getItem(getStepKey(mode));
  const n = parseInt(raw, 10);
  return Number.isInteger(n) && n >= 1 && n <= LAST_STEP ? n : null;
}

/* -------------------------------------------------------------- account */

export function markAccountComplete(email, hostId) {
  draftStore.setItem(ACCOUNT_COMPLETE_KEY, 'true');
  if (hasText(email)) draftStore.setItem(ACCOUNT_EMAIL_KEY, normalizeEmail(email));
  if (hasText(hostId)) draftStore.setItem(ACCOUNT_HOST_ID_KEY, String(hostId));
}

export function isAccountComplete() {
  return draftStore.getItem(ACCOUNT_COMPLETE_KEY) === 'true';
}

/** True when signup already succeeded for exactly this email — skip the signup call. */
export function isAccountCreatedFor(email) {
  if (!isAccountComplete()) return false;
  const created = draftStore.getItem(ACCOUNT_EMAIL_KEY);
  if (!hasText(created)) return false;
  return created === normalizeEmail(email);
}

/* ------------------------------------------------------------- snapshot */

export function readDraftSnapshot() {
  const snap = {};
  [...ALL_DRAFT_KEYS, DRAFT_TS_KEY].forEach((key) => {
    const v = draftStore.getItem(key);
    if (v != null) snap[key] = v;
  });
  return snap;
}

export function restoreDraftSnapshot(snap) {
  if (!snap || typeof snap !== 'object') return;
  Object.entries(snap).forEach(([key, value]) => {
    if (value != null) draftStore.setItem(key, String(value));
  });
}

export function preserveOnboardingDraft() {
  return readDraftSnapshot();
}

export function restoreOnboardingDraft(snapshot) {
  restoreDraftSnapshot(snapshot);
}

export function clearListingDraft() {
  [...ALL_DRAFT_KEYS, DRAFT_TS_KEY].forEach((key) => draftStore.removeItem(key));
  const ss = safeSessionStorage();
  if (ss) {
    ALL_DRAFT_KEYS.forEach((key) => ss.removeItem(key));
    ss.removeItem('basePrice');
    ss.removeItem('ratePriceOverride');
  }
}

/* --------------------------------------------------------------- resume */

/** True when the user has entered real wizard data (not defaults / empty draft). */
export function hasMeaningfulListingProgress() {
  const textKeys = [
    'propertyName', 'selectedPropertyType', 'address', 'city', 'state', 'pincode',
    'propertyDescription',
    'fullName', 'accountEmail', 'accountPhone',
    'placeSearchInput', 'latitude', 'longitude',
  ];
  if (textKeys.some((k) => hasText(draftStore.getItem(k)))) return true;

  const jsonKeys = [
    'selectedAmenities',
    'selectedHotelPolicies',
    'selectedCancellationPolicies',
  ];
  if (jsonKeys.some((k) => parseJsonArray(k).length > 0)) return true;

  if (parseJsonArray('roomTypes').some(roomHasAnyInput)) return true;
  return false;
}

/** Logged-in extranet users can skip create-account, but that alone is not a "resume". */
export function canSkipAccountStep(mode, tokenPresent) {
  if (mode === 'add-property') return true;
  return isAccountComplete() || !!tokenPresent;
}

export function shouldShowResumeBanner(mode, step) {
  const first = getFirstStep(mode);
  if (step <= first || !hasMeaningfulListingProgress()) return false;
  return true;
}

export function computeInitialStep(mode, { tokenPresent = false } = {}) {
  const first = getFirstStep(mode);
  const hasProgress = hasMeaningfulListingProgress();
  let accountDone = isAccountComplete();

  // Drop stale flags left from a prior bug (account marked done with no saved fields).
  if (accountDone && !hasProgress && mode === 'signup' && !tokenPresent) {
    draftStore.removeItem(ACCOUNT_COMPLETE_KEY);
    draftStore.removeItem(STEP_KEY_SIGNUP);
    accountDone = false;
  }

  const skipAccount = canSkipAccountStep(mode, tokenPresent);

  if (hasProgress || accountDone) {
    let step = resolveResumeStep(mode, accountDone || skipAccount);
    if (mode === 'signup' && skipAccount && step < STEPS.TYPE) step = STEPS.TYPE;
    return step;
  }
  if (skipAccount) return STEPS.TYPE;
  return first;
}

/** First step that still needs data, or the images step if everything through rooms is filled. */
export function inferResumeStep(mode, accountComplete) {
  const first = getFirstStep(mode);
  if (mode === 'signup' && !accountComplete && first === STEPS.ACCOUNT) return STEPS.ACCOUNT;

  if (!hasText(draftStore.getItem('propertyName')) || !hasText(draftStore.getItem('selectedPropertyType'))) {
    return Math.max(first, STEPS.TYPE);
  }
  if (
    !hasText(draftStore.getItem('address')) ||
    !hasText(draftStore.getItem('city')) ||
    !hasText(draftStore.getItem('state'))
  ) {
    return STEPS.LOCATION;
  }
  if (!hasText(draftStore.getItem('propertyDescription'))) {
    return STEPS.DETAILS;
  }
  const amenities = parseJsonArray('selectedAmenities');
  const policies = parseJsonArray('selectedHotelPolicies');
  if (!amenities.length || !policies.length) return STEPS.AMENITIES;

  // Photos are next and are held only in memory, so never infer past IMAGES.
  return STEPS.IMAGES;
}

export function resolveResumeStep(mode, accountComplete) {
  const first = getFirstStep(mode);
  const saved = loadSavedStep(mode);
  const inferred = inferResumeStep(mode, accountComplete);
  let step = saved != null ? saved : inferred;
  if (mode === 'signup' && accountComplete && step < STEPS.TYPE) step = STEPS.TYPE;
  if (step < first) step = first;
  // Property & room images are required and held only in memory; on reload they are gone,
  // so never resume past the images step.
  if (step > STEPS.IMAGES) step = STEPS.IMAGES;
  if (step > LAST_STEP) step = LAST_STEP;
  return step;
}

/** A draft started under a different account email must not be resumed by this login. */
function draftBelongsTo(loginEmail) {
  if (!hasText(loginEmail)) return true;
  const draftEmail = draftStore.getItem('accountEmail');
  if (!hasText(draftEmail)) return true;
  return normalizeEmail(draftEmail) === normalizeEmail(loginEmail);
}

export function hasListingDraftToResume(loginEmail) {
  prepareDraftStore();
  if (!hasMeaningfulListingProgress()) return false;
  if (!draftBelongsTo(loginEmail)) return false;
  if (isAccountComplete()) return true;
  return LISTING_FIELD_KEYS.some((key) => {
    if (JSON_FIELD_KEYS.has(key)) {
      return parseJsonArray(key).length > 0;
    }
    return hasText(draftStore.getItem(key));
  });
}

export function hasAddPropertyDraftToResume(loginEmail) {
  prepareDraftStore();
  if (!hasMeaningfulListingProgress()) return false;
  if (!draftBelongsTo(loginEmail)) return false;
  const step = loadSavedStep('add-property');
  if (step != null && step >= STEPS.TYPE) return true;
  return hasText(draftStore.getItem('propertyName')) || hasText(draftStore.getItem('address'));
}
